import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import AvaliacaoDialog from '../components/AvaliacaoDialog';
import OptimizedImage from '../components/OptimizedImage';
import { mockProfessores } from '../data/mock';

const Equipa = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const { hash } = useLocation();

  // Scroll suave para secção/card quando hash estiver presente
  // (ex: /equipa#conheca-equipa ou /equipa#ruimarques)
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      // Cards individuais: centrar verticalmente. Secções gerais: alinhar ao topo.
      const isCard = ['ruimarques', 'manuelgonzalez', 'dianasantos'].includes(id);
      el.scrollIntoView({ behavior: 'smooth', block: isCard ? 'center' : 'start' });
    }, 120);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="min-h-screen bg-white">
      <Header onOpenForm={() => setIsFormOpen(true)} />
      
      {/* Page Header */}
      <PageHeader 
        title="EQUIPA"
        subtitle="Descobre a equipa por trás dos teus resultados."
        backgroundImage="/assets/equipa-hero.webp"
        backgroundPosition="center 20%"
        overlayOpacity={0.55}
      />

      {/* Quem Somos Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
            <div className="order-2 md:order-1">
              <img
                src="/assets/gym-interior.webp"
                alt="Estúdio Ultimate Fit"
                loading="lazy"
                className="w-full h-auto aspect-[4/3] object-cover shadow-2xl"
              />
            </div>
            <div className="order-1 md:order-2 text-center md:text-left">
              <h2 className="text-4xl md:text-5xl lg:text-6xl text-black mb-6" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}>
                OS NOSSOS PROFISSIONAIS
              </h2>
              <div className="w-24 h-1 bg-yellow-400 mx-auto md:mx-0 mb-8"></div>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300, letterSpacing: "0.01em", lineHeight: "1.7" }}>
                Somos uma equipa de Personal Trainers certificados com vários anos de experiência dedicados ao treino personalizado.
              </p>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300, letterSpacing: "0.01em", lineHeight: "1.7" }}>
                Cada pessoa é única, por isso adaptamos o treino à sua realidade, respeitando sempre a sua condição física atual e os seus objetivos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="conheca-equipa" className="py-16 md:py-24 bg-white scroll-mt-24 md:scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl text-black mb-6" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}>
              CONHEÇA A EQUIPA ULTIMATE
            </h2>
            <div className="w-24 h-1 bg-yellow-400 mx-auto"></div>
          </div>

          {/* Team Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {mockProfessores.map((trainer) => (
              <Link
                key={trainer.id}
                id={trainer.slug || `trainer-${trainer.id}`}
                to={`/${trainer.slug || trainer.id}`}
                className="group cursor-pointer scroll-mt-24 md:scroll-mt-28"
              >
                <div className="relative overflow-hidden bg-black aspect-[3/4]">
                  {/* Trainer Image */}
                  <OptimizedImage
                    src={trainer.foto}
                    alt={trainer.nome}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-[400ms] ease-out"
                    style={{ objectPosition: 'center 18%' }}
                    loading="lazy"
                  />
                  
                  {/* Dark overlay on hover */}
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-[400ms]"></div>
                </div>

                {/* Trainer Info */}
                <div className="mt-6 text-center">
                  <h3 className="text-2xl md:text-3xl text-black group-hover:text-yellow-400 transition-colors duration-300 mb-2" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
                    {trainer.nome}
                  </h3>
                  <p className="text-base md:text-lg text-gray-600" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.01em" }}>
                    {trainer.role}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {/* CTA Section */}
          <div className="text-center mt-16">
            <Button
              onClick={() => setIsFormOpen(true)}
              className="btn-mobile-cta bg-yellow-400 hover:bg-yellow-500 text-black uppercase px-10 py-6 border-2 border-yellow-400 hover:shadow-2xl transform hover:-translate-y-1 transition-all"
              style={{
                fontFamily: "'BebasNeue-Book', sans-serif",
                fontWeight: "normal",
                fontSize: "33.6px",
                letterSpacing: "-0.02em"
              }}
            >
              MARCAR AVALIAÇÃO FÍSICA GRATUITA
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <AvaliacaoDialog isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </div>
  );
};

export default Equipa;
